import type { IProductRepository, IWarehouseRepository } from '../../domain/interfaces';
import type { IStockService } from '../../domain/services/interfaces';
import type { InventoryItemDTO, StockStatus } from '../dtos/inventory/InventoryItemDTO';

export type { InventoryItemDTO, StockStatus };

export interface GetInventorySnapshotUseCase {
  execute(filters?: { warehouseId?: string }): Promise<InventoryItemDTO[]>;
}

export default class GetInventorySnapshotUseCaseImpl implements GetInventorySnapshotUseCase {
  constructor(
    private readonly productRepository: IProductRepository,
    private readonly warehouseRepository: IWarehouseRepository,
    private readonly stockService: IStockService
  ) {}

  async execute(filters?: { warehouseId?: string }): Promise<InventoryItemDTO[]> {
    try {
      const products = await this.productRepository.findActive();

      if (products.length === 0) {
        return [];
      }

      const items = await Promise.all(
        products.map(async (product) => {
          const [available, reserved] = await Promise.all([
            this.stockService.getAvailableStock(product._id, filters?.warehouseId),
            this.stockService.getReservedStock(product._id, filters?.warehouseId),
          ]);

          const availableValue = available.value;
          const reservedValue = reserved.value;
          const projectedStock = Math.max(0, availableValue - reservedValue);

          let stockStatus: StockStatus;
          if (availableValue === 0) {
            stockStatus = 'OUT_OF_STOCK';
          } else if (availableValue <= 10) {
            stockStatus = 'LOW_STOCK';
          } else {
            stockStatus = 'NORMAL';
          }

          return {
            productId: product._id,
            productName: product.name,
            productSku: product.sku,
            availableStock: availableValue,
            reservedStock: reservedValue,
            projectedStock,
            unit: available.unit || 'units',
            stockStatus,
          };
        })
      );

      return items.sort((a, b) => a.productName.localeCompare(b.productName));
    } catch (error) {
      throw new Error(
        `Failed to get inventory snapshot: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}
